<nav #bar [class]="barClasses" [attr.aria-label]="label()">
  <div [class]="rowClasses">
    <ng-template #tabBox let-tab>
      <div
        [attr.data-fit]="fits(tab)"
        [class]="tabClasses(tab)"
        (mousedown)="onTabMouseDown($event, tab)"
        (auxclick)="onTabAuxClick($event, tab)"
        [style.width.px]="fits(tab) === 'flex' ? stripWidth() : null"
        [mibTooltip]="tab.iconOnly ? tab.label : check.tooltipText()"
        [mibTooltipPosition]="tooltipPosition()"
        [mibTooltipShowDelay]="tooltipShowDelay()">
        <a
          data-mib-tab
          [class]="labelClasses(tab)"
          [attr.href]="leads(tab) ? tab.href : null"
          [attr.role]="leads(tab) ? null : 'button'"
          [attr.tabindex]="leads(tab) ? null : isOff(tab) ? -1 : 0"
          [attr.aria-disabled]="isOff(tab) || null"
          [attr.aria-current]="isOpen(tab) ? (leads(tab) ? 'page' : 'true') : null"
          draggable="false"
          [attr.aria-label]="tab.iconOnly ? tab.label : null"
          [attr.aria-busy]="tab.loading || null"
          [attr.aria-describedby]="tab.loading ? loadingId : null"
          (click)="onTabClick($event, tab)"
          (keydown.enter)="onTabKey($event, tab)"
          (keydown.space)="onTabKey($event, tab)">
          @if (tab.loading) {
            <span
              class="pointer-events-none flex size-5 shrink-0 items-center justify-center"
              aria-hidden="true"
              [animate.leave]="tab.icon || tab.dot ? 'mib-spinner-fade' : 'mib-spinner-collapse'">
              <mib-spinner size="sm" />
            </span>
          } @else if (tab.dot) {
            <span class="flex shrink-0" [animate.enter]="heirs().has(tab.value) ? 'mib-spinner-heir' : ''">
              <span [class]="dotClasses(tab)" [attr.aria-label]="tab.dotLabel || null"></span>
            </span>
          }
          @if (tab.icon && !tab.loading) {
            <!--
             Transparent to the pointer: the primitive resolves a press by asking whether its
             target is an HTML element before looking for the tab around it, and a drawing node
             is not one — so a press landing on the icon was dropped without a word, and an
             icon-only tab could never be opened at all.
            -->
            <svg
              mib-icon
              [name]="tab.icon"
              class="pointer-events-none size-5 shrink-0"
              [class.not-expanded:size-4]="tab.iconOnly"
              aria-hidden="true"></svg>
          }

          <span [class]="tab.iconOnly ? 'sr-only' : 'flex min-w-0 flex-col text-left'">
            <span #check="checkTruncate" [mibCheckTruncate]="tab.label" class="truncate text-sm/5 font-medium">{{
              tab.label
            }}</span>
            @if (tab.subLabel && !tab.iconOnly && subLabels() === 'row') {
              <span class="truncate text-xs/4.5 text-gray-500">{{ tab.subLabel }}</span>
            }
          </span>
        </a>

        @if (tab.closable) {
          <button
            type="button"
            [class]="crossClasses"
            [attr.aria-label]="closeLabel()"
            (click)="closed.emit(tab.value)">
            <svg mib-icon name="x" class="size-4"></svg>
          </button>
        }
      </div>
    </ng-template>

    @for (tab of leading(); track tab.value) {
      <ng-container *ngTemplateOutlet="tabBox; context: { $implicit: tab }" />
    }
    <div
      #viewport
      [class]="viewportClasses()"
      (pointerdown)="onPointerDown($event)"
      (pointermove)="onPointerMove($event)"
      (pointerup)="onPointerEnd($event)"
      (pointercancel)="onPointerEnd($event)"
      (dragstart)="$event.preventDefault()"
      (wheel)="onWheel($event)">
      <div
        [class]="stripClasses()"
        [style.transform]="stripOffset() === null ? null : 'translateX(' + -stripOffset()! + 'px)'"
        [style.transition-duration.ms]="snapping() ? snapMs() : null"
        (transitionend)="onGlideEnd($event)">
        @for (tab of drawnFlex(); track tab.value) {
          <ng-container *ngTemplateOutlet="tabBox; context: { $implicit: tab }" />
        }
      </div>
    </div>
    @for (tab of trailing(); track tab.value) {
      <ng-container *ngTemplateOutlet="tabBox; context: { $implicit: tab }" />
    }
  </div>

  @if (leftOff()) {
    <button #arrow type="button" [mibMenuTrigger]="openTabs" [class]="moreClasses()" [attr.aria-label]="moreLabel()">
      <svg mib-icon name="chevron-down" class="size-5"></svg>
    </button>
    <mib-menu
      #openTabs
      align="end"
      fullWidthOnPhone
      [markSelected]="marker() === 'line'"
      [menuItems]="menuEntries()"
      [removeLabel]="closeLabel()"
      [loadingLabel]="loadingLabel()"
      (itemSelected)="reveal($event)"
      (itemRemoved)="closed.emit($event)">
      @if (overflowTitle()) {
        <span mibMenuHeader [class]="menuTitleClasses">{{ overflowTitle() }}</span>
      }
    </mib-menu>
  }
</nav>

<!-- Asks, in a container sized by its content, for every tab at its floor: never for the window it draws. -->
<div aria-hidden="true" class="flex h-0 flex-wrap overflow-hidden">
  <div class="shrink-0" [style.width.px]="contentWidth()"></div>
  @for (tab of rowFlex(); track tab.value) {
    <div [class]="'shrink-0 ' + floorClasses()"></div>
  }
</div>

<span [id]="loadingId" class="sr-only">{{ loadingLabel() }}</span>
